import { inject, Injectable } from "@angular/core";
import { Shortcut } from "app/layout/common/shortcuts/shortcuts.types";
import { ApiResponse } from "app/shared/models";
import { HttpClientService } from "app/shared/services";
import { map, Observable, ReplaySubject, switchMap, take, tap, throwError } from "rxjs";

@Injectable({ providedIn: "root" })
export class ShortcutsService {
  private _clientService = inject(HttpClientService);
  private _shortcuts: ReplaySubject<Shortcut[]> = new ReplaySubject<Shortcut[]>(1);

  /**
   * Constructor
   */
  constructor() {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for shortcuts
   */
  get shortcuts$(): Observable<Shortcut[]> {
    return this._shortcuts.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all messages
   */
  getAll = (): Observable<ApiResponse<Shortcut[]>> => {
    return this._clientService.get<ApiResponse<Shortcut[]>>("/webapp/shortcuts").pipe(
      tap((response: ApiResponse<Shortcut[] | null>) => {
        if (!response.success) {
          return throwError(() => new Error(response.errors.join(",")));
        }

        this._shortcuts.next(response.data);
      })
    );
  };

  /**
   * Create a shortcut
   *
   * @param shortcut
   */
  create = (shortcut: Shortcut): Observable<Shortcut> => {
    return this.shortcuts$.pipe(
      take(1),
      switchMap(() =>
        this._clientService
          .post<Shortcut>("/webapp/shortcuts", {
            ...shortcut,
            useRouter: false,
          })
          .pipe(
            map((newShortcut) => {
              // Return the new shortcut from observable
              return newShortcut;
            })
          )
      )
    );
  };

  /**
   * Update the shortcut
   *
   * @param id
   * @param shortcut
   */
  update = (id: string, shortcut: Shortcut): Observable<Shortcut> => {
    return this.shortcuts$.pipe(
      take(1),
      switchMap(() =>
        this._clientService.put<Shortcut>(`/webapp/shortcuts/${id}`, shortcut).pipe(
          map((updatedShortcut: Shortcut) => {
            // Return the updated shortcut
            return updatedShortcut;
          })
        )
      )
    );
  };

  /**
   * Delete the shortcut
   *
   * @param id
   */
  delete = (id: string): Observable<boolean> => {
    return this.shortcuts$.pipe(
      take(1),
      switchMap(() =>
        this._clientService.delete<boolean>(`/webapp/shortcuts/${id}`).pipe(
          map((isDeleted: boolean) => {
            // Return the deleted status
            return isDeleted;
          })
        )
      )
    );
  };
}
